import Color from '@/Enums/Color.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import type { IFootTrafficVisitsHistoryRecord } from '@/Interfaces/Attribution/FootTraffic.interface';
import { buildVisitsByWeek, priorYearWeekKey, weekKey } from '@/helpers/attribution/fiscalWeek';
import { Box, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { useMemo } from 'react';

interface WeekChange {
  weekStart: string;
  current: number;
  prior: number;
  yoyPercent: number;
  isTopSwing: boolean;
}

const TOP_SWING_COUNT = 3;
const STRIP_HEIGHT = 40;

interface Props {
  dateRangeStart: string;
  dateRangeEnd: string;
  visitsHistory: IFootTrafficVisitsHistoryRecord[];
}

const YoyChangeStrip = ({ dateRangeStart, dateRangeEnd, visitsHistory }: Props) => {
  const weekChanges = useMemo<WeekChange[]>(() => {
    if (!visitsHistory.length) return [];
    const visitsByWeek = buildVisitsByWeek(visitsHistory);
    const start = moment(dateRangeStart).startOf('isoWeek');
    const end = moment(dateRangeEnd).startOf('isoWeek');
    const weeks: WeekChange[] = [];
    for (let w = start.clone(); w.isSameOrBefore(end); w.add(1, 'week')) {
      const wkKey = weekKey(w.toDate());
      const current = visitsByWeek.get(wkKey);
      const prior = visitsByWeek.get(priorYearWeekKey(w.toDate()));
      if (current === undefined || !prior) continue;
      weeks.push({
        weekStart: wkKey,
        current,
        prior,
        yoyPercent: ((current - prior) / prior) * 100,
        isTopSwing: false,
      });
    }
    const topSwings = [...weeks]
      .sort((a, b) => Math.abs(b.yoyPercent) - Math.abs(a.yoyPercent))
      .slice(0, TOP_SWING_COUNT)
      .map(w => w.weekStart);
    return weeks.map(w => ({ ...w, isTopSwing: topSwings.includes(w.weekStart) }));
  }, [dateRangeStart, dateRangeEnd, visitsHistory]);

  const maxAbs = useMemo(() => Math.max(1, ...weekChanges.map(w => Math.abs(w.yoyPercent))), [weekChanges]);

  if (!weekChanges.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        Weekly YoY % change — top {TOP_SWING_COUNT} swings highlighted
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: STRIP_HEIGHT }}>
        {weekChanges.map(w => {
          const positive = w.yoyPercent >= 0;
          const base = positive ? Color.LightGreen : Color.Orange;
          const color = w.isTopSwing ? base : `${base}66`;
          const heightPct = (Math.abs(w.yoyPercent) / maxAbs) * 100;
          return (
            <Tooltip
              key={w.weekStart}
              title={
                <Box>
                  <div>Week of {moment(w.weekStart).format(DateFormat.ShortMonthDay)}</div>
                  <div>Current: {w.current.toLocaleString()}</div>
                  <div>Prior year: {w.prior.toLocaleString()}</div>
                  <div>
                    YoY: {w.yoyPercent >= 0 ? '+' : ''}
                    {w.yoyPercent.toFixed(1)}%
                  </div>
                </Box>
              }
              placement='top'
              arrow
            >
              <Box
                sx={{
                  flex: 1,
                  height: `${Math.max(heightPct, 6)}%`,
                  backgroundColor: color,
                  borderRadius: '2px',
                  alignSelf: positive ? 'flex-end' : 'flex-start',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s',
                  '&:hover': { opacity: 0.8 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default YoyChangeStrip;
